import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import imageCompression from "browser-image-compression";
import {
  Camera,
  Clapperboard,
  PenLine,
  BookOpen,
  Briefcase,
  Search,
  MessageSquare,
  Tag,
  CloudUpload,
  Image as ImageIcon,
  Video,
  ChevronDown,
  X,
} from "lucide-react";
import { getApiBaseUrl } from "../config/api";
import { resolveAvatarUrl, handleAvatarError } from "../utils/profilePicUrl";
import { compressVideo, formatBytes } from "../utils/compressVideo";
import UploadProgressBar from "./shared/UploadProgressBar";
import MentionInput from "./shared/MentionInput";
import ArticleForm from "./ArticleForm";
import BlogForm from "./BlogForm";
import HiringForm from "./HiringForm";
import LookingForJobForm from "./LookingForJobForm";
import QuestionForm from "./QuestionForm";
import { isEmployer, canApplyForJobs } from "../utils/userRole";
import { appendMentionedField } from "../utils/mentions";
import MediaComposePreview, {
  REORDER_DRAG_TYPE,
} from "./shared/MediaComposePreview";
import CaptionAssist from "./shared/CaptionAssist";
import "./PostForm.css";

const API_URL = getApiBaseUrl();
const MEDIA_ACCEPT =
  "image/*,video/*,.jpg,.jpeg,.jpe,.jfif,.pjpeg,.pjp,.png,.gif,.webp,.bmp,.svg,.tif,.tiff,.avif,.heic,.heif,.ico,.mp4,.m4v,.mov,.qt,.webm,.ogg,.ogv,.mpeg,.mpg,.mpe,.avi,.mkv,.3gp,.3g2,.flv,.wmv,.asf,.ts,.m2ts,.mts";
const IMAGE_ACCEPT =
  "image/*,.jpg,.jpeg,.jpe,.jfif,.pjpeg,.pjp,.png,.gif,.webp,.bmp,.svg,.tif,.tiff,.avif,.heic,.heif,.ico";
const VIDEO_ACCEPT =
  "video/*,.mp4,.m4v,.mov,.qt,.webm,.ogg,.ogv,.mpeg,.mpg,.mpe,.avi,.mkv,.3gp,.3g2,.flv,.wmv,.asf,.ts,.m2ts,.mts";

const isImageFile = (file) =>
  file.type.startsWith("image/") || /\.(jpe?g|jpe|jfif|pjpeg|pjp|png|gif|webp|bmp|svg|tiff?|avif|heic|heif|ico)$/i.test(file.name);

const isVideoFile = (file) =>
  file.type.startsWith("video/") || /\.(mp4|m4v|mov|qt|webm|ogg|ogv|mpe?g|mpg|avi|mkv|3gp|3g2|flv|wmv|asf|ts|m2ts|mts)$/i.test(file.name);

const isReorderDrag = (e) =>
  Array.from(e.dataTransfer?.types || []).includes(REORDER_DRAG_TYPE);

function PostForm({ onSubmit, user, onRefresh }) {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [mediaFiles, setMediaFiles] = useState([]);
  const [category, setCategory] = useState("General");
  const [loading, setLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState("");
  const [uploadProgress, setUploadProgress] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showArticleForm, setShowArticleForm] = useState(false);
  const [showBlogForm, setShowBlogForm] = useState(false);
  const [showHiringForm, setShowHiringForm] = useState(false);
  const [showLookingForJobForm, setShowLookingForJobForm] = useState(false);
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  /** text | photo | video — controls accept filter + post_type */
  const [composerMode, setComposerMode] = useState("text");
  const canPostHiring = isEmployer(user);
  const canPostLookingForJob = canApplyForJobs(user);
  const [isDragging, setIsDragging] = useState(false);
  const previewUrlsRef = useRef(new Map());
  const uploadAbortRef = useRef(null);
  const uploadCancelledRef = useRef(false);

  const mediaAccept =
    composerMode === "photo"
      ? IMAGE_ACCEPT
      : composerMode === "video"
        ? VIDEO_ACCEPT
        : MEDIA_ACCEPT;

  const openComposer = (mode = "text") => {
    setComposerMode(mode);
    setShowModal(true);
  };

  const addFiles = (files) =>
    setMediaFiles((prev) => [
      ...prev,
      ...Array.from(files).map((file) => ({
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        file,
      })),
    ]);
  const removeFile = (idx) =>
    setMediaFiles((prev) => prev.filter((_, i) => i !== idx));
  const reorderFiles = (fromIndex, toIndex) => {
    if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) return;
    setMediaFiles((prev) => {
      if (fromIndex >= prev.length || toIndex >= prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  };
  const moveFile = (idx, direction) =>
    reorderFiles(idx, idx + direction);

  const handleFileChange = (e) => addFiles(Array.from(e.target.files));

  const handleSubmit = async (e) => {
  e.preventDefault();
  if (!user?.firstname && !user?.email) return alert("You must be logged in");

  // Cancel any previous in-flight upload
  if (uploadAbortRef.current) {
    uploadAbortRef.current.abort();
  }
  const abortController = new AbortController();
  uploadAbortRef.current = abortController;
  uploadCancelledRef.current = false;

  setLoading(true);
  setUploadProgress(0);
  setSubmitStatus("Preparing post...");
  try {
    const formData = new FormData();

    // 👇 now sending firstname instead of username
    user.firstname && formData.append("firstname", user.firstname);
    user.lastname && formData.append("lastname", user.lastname);
    user.email && formData.append("email", user.email);
    user.country && formData.append("country", user.country);

    formData.append("title", title.trim());
    appendMentionedField(formData, "content", content.trim());
    formData.append("category", category);
    user.username && formData.append("username", user.username);

    let resolvedType = composerMode;
    if (resolvedType === "text" && mediaFiles.length > 0) {
      const onlyImages = mediaFiles.every((item) => isImageFile(item.file));
      const onlyVideos = mediaFiles.every((item) => isVideoFile(item.file));
      if (onlyImages) resolvedType = "photo";
      else if (onlyVideos) resolvedType = "video";
    }
    formData.append("post_type", resolvedType);

    const eligibleMedia = mediaFiles.filter((item) => {
      const file = item.file;
      if (composerMode === "photo") return isImageFile(file);
      if (composerMode === "video") return isVideoFile(file);
      return isImageFile(file) || isVideoFile(file);
    });
    const totalMedia = eligibleMedia.length;

    for (let i = 0; i < eligibleMedia.length; i++) {
      if (uploadCancelledRef.current) {
        throw new DOMException("Upload cancelled", "AbortError");
      }
      const file = eligibleMedia[i].file;
      const prepPct = Math.round(((i + 0.5) / Math.max(totalMedia, 1)) * 40);
      if (isImageFile(file)) {
        try {
          setSubmitStatus(
            totalMedia > 1
              ? `Compressing photo ${i + 1} of ${totalMedia}…`
              : `Compressing photo…`
          );
          setUploadProgress(prepPct);
          const compressed = await imageCompression(file, {
            maxSizeMB: 1.5,
            maxWidthOrHeight: 1920,
            useWebWorker: true,
            onProgress: (pct) => {
              setSubmitStatus(
                totalMedia > 1
                  ? `Compressing photo ${i + 1} of ${totalMedia}… ${pct}%`
                  : `Compressing photo… ${pct}%`
              );
              setUploadProgress(
                Math.min(
                  39,
                  prepPct + Math.round((pct / 100) * (40 / Math.max(totalMedia, 1)))
                )
              );
            },
          });
          formData.append("media[]", compressed, compressed.name);
        } catch (err) {
          console.warn("Image compression failed, uploading original:", err);
          formData.append("media[]", file);
        }
      } else if (isVideoFile(file)) {
        try {
          const originalSize = formatBytes(file.size);
          setSubmitStatus(
            totalMedia > 1
              ? `Compressing video ${i + 1} of ${totalMedia} (${originalSize})…`
              : `Compressing video (${originalSize})…`
          );
          setUploadProgress(prepPct);
          const result = await compressVideo(file, {
            onProgress: (pct) => {
              setSubmitStatus(
                totalMedia > 1
                  ? `Compressing video ${i + 1} of ${totalMedia}… ${pct}%`
                  : `Compressing video… ${pct}%`
              );
              setUploadProgress(
                Math.min(
                  39,
                  prepPct + Math.round((pct / 100) * (40 / Math.max(totalMedia, 1)))
                )
              );
            },
          });
          const compressedVideo = result.file || result;
          if (result.mode === "server") {
            setSubmitStatus(
              result.message ||
                `Uploading ${originalSize} — server will convert…`
            );
          } else if (result.mode === "compressed") {
            setSubmitStatus(
              result.message ||
                `Compressed ${originalSize} → ${formatBytes(compressedVideo.size)}`
            );
          } else {
            setSubmitStatus(`Ready ${originalSize}`);
          }
          formData.append("media[]", compressedVideo, compressedVideo.name);
        } catch (err) {
          console.warn("Video prep failed:", err);
          throw new Error(
            err?.message ||
              `Could not prepare "${file.name}" for upload. Use a smaller H.264 MP4 (under 80MB).`
          );
        }
      } else {
        console.warn("Unsupported file type skipped:", file.name);
      }
    }

    if (uploadCancelledRef.current) {
      throw new DOMException("Upload cancelled", "AbortError");
    }

    setSubmitStatus(
      totalMedia > 0 ? "Uploading media…" : "Posting…"
    );
    setUploadProgress(totalMedia > 0 ? 40 : 10);
    const { data } = await axios.post(
      `${API_URL}/create_post.php`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
        timeout: 0,
        signal: abortController.signal,
        onUploadProgress: (event) => {
          const total = event.total || event.loaded;
          if (!total) return;
          const ratio = Math.min(1, event.loaded / total);
          const uploadPct = Math.round(ratio * 60);
          setUploadProgress(Math.min(100, 40 + uploadPct));
          const pct = Math.round(ratio * 100);
          const loadedLabel = formatBytes(event.loaded);
          const totalLabel = formatBytes(total);
          setSubmitStatus(
            totalMedia > 0
              ? `Uploading media… ${pct}% (${loadedLabel} / ${totalLabel})`
              : `Posting… ${pct}%`
          );
        },
      }
    );

    if (uploadCancelledRef.current) {
      return;
    }

    setUploadProgress(100);
    setSubmitStatus("Finishing…");

    if (data.success) {
      const msg = data.message || "Post submitted!";
      alert(msg);
      onSubmit?.(data.post);
      setTitle("");
      setContent("");
      setMediaFiles([]);
      setCategory("General");
      setComposerMode("text");
      setShowModal(false);
      onRefresh?.();
    } else {
      alert(data.message || "Post failed");
    }
  } catch (err) {
    if (
      uploadCancelledRef.current ||
      err?.name === "CanceledError" ||
      err?.name === "AbortError" ||
      err?.code === "ERR_CANCELED"
    ) {
      setSubmitStatus("Upload cancelled");
      return;
    }
    console.error(err);
    alert("Upload error: " + (err.response?.data?.message || err.message));
  } finally {
    if (uploadAbortRef.current === abortController) {
      uploadAbortRef.current = null;
    }
    setLoading(false);
    setSubmitStatus("");
    setUploadProgress(null);
  }
};

  const cancelUpload = () => {
    uploadCancelledRef.current = true;
    if (uploadAbortRef.current) {
      uploadAbortRef.current.abort();
    }
    setSubmitStatus("Cancelling…");
  };

  const handleInput = (e) => {
    setContent(e.target.innerHTML); // Keeps HTML formatting
  };

  useEffect(() => {
    const urls = previewUrlsRef.current;
    const activeIds = new Set(mediaFiles.map((item) => item.id));

    mediaFiles.forEach((item) => {
      if (!urls.has(item.id)) {
        urls.set(item.id, URL.createObjectURL(item.file));
      }
    });

    for (const [id, url] of urls.entries()) {
      if (!activeIds.has(id)) {
        URL.revokeObjectURL(url);
        urls.delete(id);
      }
    }
  }, [mediaFiles]);

  useEffect(
    () => () => {
      previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      previewUrlsRef.current.clear();
    },
    []
  );

  const Preview = () => {
    if (mediaFiles.length === 0) return null;
    const items = mediaFiles.map((item) => ({
      id: item.id,
      url: previewUrlsRef.current.get(item.id),
      isVideo: isVideoFile(item.file),
    }));
    return (
      <MediaComposePreview
        items={items}
        onRemove={removeFile}
        onReorder={reorderFiles}
        onMove={moveFile}
      />
    );
  };

  return (
    <div style={{ width: "100%", maxWidth: "100%", margin: 0, padding: 0, boxSizing: "border-box" }}>
      {/* Compact create-post bar – LinkedIn style */}
      <div className="pf-compose">
        {/* Top row: avatar + input */}
        <div
          className="pf-compose-top"
          onClick={() => openComposer("text")}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") openComposer("text");
          }}
        >
          <img
            src={resolveAvatarUrl(user, API_URL)}
            alt="avatar"
            className="pf-compose-avatar"
            onError={handleAvatarError}
          />

          <div className="pf-compose-prompt">
            What&apos;s on your mind, {user?.firstname || "Friend"}?
          </div>
        </div>

        {/* Bottom row: Photo / Video / Write article */}
        <div className="pf-compose-actions">
          {[
            {
              key: "photo",
              label: "Photo",
              Icon: Camera,
              onClick: () => {
                setComposerMode("photo");
                const input = document.getElementById("mediaInputTriggerPhoto");
                if (input) {
                  input.value = "";
                  input.click();
                }
              },
            },
            {
              key: "video",
              label: "Video",
              Icon: Clapperboard,
              onClick: () => {
                setComposerMode("video");
                const input = document.getElementById("mediaInputTriggerVideo");
                if (input) {
                  input.value = "";
                  input.click();
                }
              },
            },
            {
              key: "article",
              label: "Write article",
              Icon: PenLine,
              onClick: () => setShowArticleForm(true),
            },
            {
              key: "blog",
              label: "Write blog",
              Icon: BookOpen,
              onClick: () => setShowBlogForm(true),
            },
            {
              key: "question",
              label: "Ask question",
              Icon: MessageSquare,
              onClick: () => setShowQuestionForm(true),
            },
            ...(canPostHiring
              ? [
                  {
                    key: "hiring",
                    label: "Post hiring",
                    Icon: Briefcase,
                    onClick: () => setShowHiringForm(true),
                  },
                ]
              : []),
            ...(canPostLookingForJob
              ? [
                  {
                    key: "looking_for_job",
                    label: "Looking for job",
                    Icon: Search,
                    onClick: () => setShowLookingForJobForm(true),
                  },
                ]
              : []),
          ].map((action) => {
            const ActionIcon = action.Icon;
            return (
            <div
              key={action.key}
              className="pf-action"
              title={action.label}
              aria-label={action.label}
              onClick={(e) => {
                e.stopPropagation();
                action.onClick();
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.stopPropagation();
                  action.onClick();
                }
              }}
            >
              <span className={`pf-action-icon ${action.key}`}>
                <ActionIcon size={15} strokeWidth={2.25} />
              </span>
              <span className="pf-action-label" aria-hidden="true">
                {action.label}
              </span>
            </div>
            );
          })}
        </div>

        <input
          type="file"
          id="mediaInputTriggerPhoto"
          accept={IMAGE_ACCEPT}
          multiple
          style={{ display: "none" }}
          onChange={(e) => {
            if (e.target.files.length > 0) {
              setComposerMode("photo");
              handleFileChange(e);
              setShowModal(true);
            }
          }}
        />
        <input
          type="file"
          id="mediaInputTriggerVideo"
          accept={VIDEO_ACCEPT}
          multiple
          style={{ display: "none" }}
          onChange={(e) => {
            if (e.target.files.length > 0) {
              setComposerMode("video");
              handleFileChange(e);
              setShowModal(true);
            }
          }}
        />
      </div>

      {showArticleForm && (
        <ArticleForm
          user={user}
          onClose={() => setShowArticleForm(false)}
          onSuccess={(post) => onSubmit?.(post)}
          onRefresh={onRefresh}
        />
      )}

      {showBlogForm && (
        <BlogForm
          user={user}
          onClose={() => setShowBlogForm(false)}
          onSuccess={(post) => onSubmit?.(post)}
          onRefresh={onRefresh}
        />
      )}

      {showHiringForm && (
        <HiringForm
          user={user}
          onClose={() => setShowHiringForm(false)}
          onSuccess={(post) => onSubmit?.(post)}
          onRefresh={onRefresh}
        />
      )}

      {showLookingForJobForm && (
        <LookingForJobForm
          user={user}
          onClose={() => setShowLookingForJobForm(false)}
          onSuccess={(post) => onSubmit?.(post)}
          onRefresh={onRefresh}
        />
      )}

      {showQuestionForm && (
        <QuestionForm
          user={user}
          onClose={() => setShowQuestionForm(false)}
          onSuccess={(post) => onSubmit?.(post)}
          onRefresh={onRefresh}
        />
      )}

      {/* Post Modal */}
      {showModal && (
        <div
          className="pf-modal-overlay"
          onClick={(e) => {
            if (loading || e.target !== e.currentTarget) return;
            setShowModal(false);
            setComposerMode("text");
          }}
        >
          <div
            className="pf-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="pf-modal-title"
          >
            <button
              type="button"
              className="pf-modal-close"
              disabled={loading}
              aria-label="Close"
              onClick={() => {
                if (loading) return;
                setShowModal(false);
                setComposerMode("text");
              }}
            >
              <X size={16} strokeWidth={2.4} />
            </button>

            <header className="pf-modal-header">
              <h2 id="pf-modal-title" className="pf-modal-title">
                {composerMode === "photo"
                  ? "Create a photo post"
                  : composerMode === "video"
                    ? "Create a video post"
                    : "Create a new post"}
              </h2>
              <p className="pf-modal-sub">
                {composerMode === "photo"
                  ? "Share one or more photos with your community."
                  : composerMode === "video"
                    ? "Share a video with your community."
                    : "Share thoughts, photos, or videos with your community."}
              </p>
            </header>

            <form className="pf-modal-form" onSubmit={handleSubmit}>
              <label className="pf-field">
                <span className="pf-field-icon" aria-hidden="true">
                  <PenLine size={16} strokeWidth={2} />
                </span>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Give your post a title"
                  required
                  className="pf-field-input"
                />
              </label>

              <div className="pf-caption-block">
                <label className="pf-field pf-field--area">
                  <span className="pf-field-icon pf-field-icon--top" aria-hidden="true">
                    <MessageSquare size={16} strokeWidth={2} />
                  </span>
                  <MentionInput
                    userId={user?.id || user?.user_id}
                    value={content}
                    onChange={setContent}
                    placeholder="What's on your mind? Type @ to mention a connection"
                    required
                    rows={5}
                    className="pf-mention-field"
                    style={{
                      border: "none",
                      borderRadius: 0,
                      padding: 0,
                      fontSize: 15,
                      lineHeight: 1.5,
                      resize: "vertical",
                      outline: "none",
                      backgroundColor: "transparent",
                      whiteSpace: "pre-wrap",
                      color: "#12283a",
                      boxShadow: "none",
                      maxWidth: "100%",
                      width: "100%",
                      minHeight: 110,
                    }}
                  />
                </label>
                <CaptionAssist
                  text={content}
                  title={title}
                  category={category}
                  userId={user?.id || user?.user_id}
                  disabled={loading}
                  onApply={setContent}
                  onApplyTitle={(nextTitle) => {
                    if (!title.trim() && nextTitle) setTitle(nextTitle);
                  }}
                />
              </div>

              <label className="pf-field pf-field--select">
                <span className="pf-field-icon" aria-hidden="true">
                  <Tag size={16} strokeWidth={2} />
                </span>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="pf-field-input pf-field-select"
                >
                  {[
                    "General",
                    "Really Lesson",
                    "COOL",
                    "ReallyAvatar",
                    "Coursera",
                    "TechTree",
                  ].map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
                <span className="pf-field-chevron" aria-hidden="true">
                  <ChevronDown size={16} strokeWidth={2.2} />
                </span>
              </label>

              <div
                className={`pf-dropzone${isDragging ? " pf-dropzone--active" : ""}`}
                onClick={() => document.getElementById("mediaInput")?.click()}
                onDragOver={(e) => {
                  if (isReorderDrag(e)) return;
                  e.preventDefault();
                  setIsDragging(true);
                }}
                onDragLeave={(e) => {
                  if (isReorderDrag(e)) return;
                  e.preventDefault();
                  setIsDragging(false);
                }}
                onDrop={(e) => {
                  if (isReorderDrag(e)) {
                    setIsDragging(false);
                    return;
                  }
                  e.preventDefault();
                  setIsDragging(false);

                  if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                    let files = Array.from(e.dataTransfer.files);
                    if (composerMode === "photo") {
                      files = files.filter(isImageFile);
                    } else if (composerMode === "video") {
                      files = files.filter(isVideoFile);
                    } else {
                      files = files.filter(
                        (f) => isImageFile(f) || isVideoFile(f)
                      );
                    }
                    if (files.length === 0) {
                      alert(
                        composerMode === "photo"
                          ? "Please drop photo files."
                          : composerMode === "video"
                            ? "Please drop video files."
                            : "Please drop photo or video files."
                      );
                      return;
                    }
                    handleFileChange({ target: { files } });
                    e.dataTransfer.clearData();
                  }
                }}
              >
                <div className="pf-dropzone-main">
                  <span className="pf-dropzone-icon" aria-hidden="true">
                    <CloudUpload size={22} strokeWidth={2} />
                  </span>
                  <div className="pf-dropzone-copy">
                    <p className="pf-dropzone-title">
                      {composerMode === "photo"
                        ? "Drag & drop photos here or "
                        : composerMode === "video"
                          ? "Drag & drop videos here or "
                          : "Drag & drop photos and videos here or "}
                      <strong>click to add</strong>
                    </p>
                    <p className="pf-dropzone-hint">
                      {composerMode === "photo"
                        ? "You can add multiple photos and arrange the order below."
                        : composerMode === "video"
                          ? "You can add multiple videos and arrange the order below."
                          : "You can mix photos and videos, then arrange the order below."}
                    </p>
                  </div>
                </div>

                <div className="pf-dropzone-pills">
                  <button
                    type="button"
                    className="pf-media-pill"
                    onClick={(e) => {
                      e.stopPropagation();
                      document.getElementById("mediaInputPhotoModal")?.click();
                    }}
                  >
                    <ImageIcon size={14} strokeWidth={2.2} />
                    Photos
                  </button>
                  <button
                    type="button"
                    className="pf-media-pill"
                    onClick={(e) => {
                      e.stopPropagation();
                      document.getElementById("mediaInputVideoModal")?.click();
                    }}
                  >
                    <Video size={14} strokeWidth={2.2} />
                    Videos
                  </button>
                </div>
              </div>

              <input
                id="mediaInput"
                type="file"
                accept={mediaAccept}
                multiple
                style={{ display: "none" }}
                onChange={(e) => {
                  handleFileChange(e);
                  e.target.value = "";
                }}
              />
              <input
                id="mediaInputPhotoModal"
                type="file"
                accept={IMAGE_ACCEPT}
                multiple
                style={{ display: "none" }}
                onChange={(e) => {
                  handleFileChange(e);
                  e.target.value = "";
                }}
              />
              <input
                id="mediaInputVideoModal"
                type="file"
                accept={VIDEO_ACCEPT}
                multiple
                style={{ display: "none" }}
                onChange={(e) => {
                  handleFileChange(e);
                  e.target.value = "";
                }}
              />

              <Preview />

              {loading && (
                <UploadProgressBar
                  status={submitStatus}
                  progress={uploadProgress ?? 0}
                  onCancel={cancelUpload}
                  hint="Keep this window open. Use Cancel upload if it is taking too long — do not only reload the page."
                />
              )}

              <button type="submit" disabled={loading} className="pf-share-btn">
                {loading
                  ? uploadProgress !== null
                    ? `Uploading ${uploadProgress}%`
                    : submitStatus || "Posting…"
                  : "Share post"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default PostForm;
